import { can, type FavoriteRow, type SpotFavoriteRow } from '@feral/shared/client'
import { type QueryClient, useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api.js'
import { useSession } from '../../lib/session.js'
import { spotKey } from '../spots/common.js'

/**
 * Your favorites, people and spots (decisions 59 and 105): one list of each, read by the
 * Favorites tab and by every star (tiles, Wall rows, cards, profiles, spot pages), so a star flipped
 * anywhere shows everywhere at once.
 */
export const FAVORITES_KEY = ['favorites'] as const
export const SPOT_FAVORITES_KEY = ['spot-favorites'] as const

/** Only vetted members favorite people (policy `favorite`): nobody else asks for the list. */
export function useFavorites() {
  const trusted = useSession((s) => s.me?.state === 'trusted')
  return useQuery({ queryKey: FAVORITES_KEY, queryFn: api.favorites, enabled: trusted, staleTime: 15_000 })
}
/** Only vetted members star spots too (policy `favorite_spot`, issue 8): the stars, the menu's row, the map's Favorites. */
export function useMayFavoriteSpots(): boolean {
  return useSession((s) => !!s.me && can({ id: s.me.id, state: s.me.state }, 'favorite_spot').allow)
}
export function useSpotFavorites() {
  const may = useMayFavoriteSpots()
  return useQuery({
    queryKey: SPOT_FAVORITES_KEY,
    queryFn: api.spotFavorites,
    enabled: may,
    staleTime: 15_000,
  })
}

/** Is this person, or this spot, one of yours; undefined until the list is in. */
export function useIsFavorite(id: string | null | undefined): boolean | undefined {
  const q = useFavorites()
  return id && q.data ? q.data.some((r) => r.id === id) : undefined
}
export function useIsSpotFavorite(id: string | null | undefined): boolean | undefined {
  const q = useSpotFavorites()
  return id && q.data ? q.data.some((r) => r.id === id) : undefined
}

/**
 * Favorite or unfavorite a person: the list changes at once (a removal drops the row; an addition
 * waits for the server's row) and is put back if the server says no.
 */
export async function setPersonFavorite(qc: QueryClient, id: string, on: boolean): Promise<void> {
  const before = qc.getQueryData<FavoriteRow[]>(FAVORITES_KEY)
  if (!on) qc.setQueryData<FavoriteRow[]>(FAVORITES_KEY, (old) => old?.filter((r) => r.id !== id))
  try {
    await (on ? api.favorite(id) : api.unfavorite(id))
  } catch (e) {
    qc.setQueryData(FAVORITES_KEY, before)
    throw e
  } finally {
    await Promise.all([
      qc.invalidateQueries({ queryKey: FAVORITES_KEY }),
      qc.invalidateQueries({ queryKey: ['user', id] }),
    ])
  }
}

/**
 * Favorite or unfavorite a spot (decision 59), from its header star, its map card, its menu, its tile
 * or the Wall: the same call and the same refresh everywhere, as a person's favorite is.
 */
export async function setSpotFavorite(qc: QueryClient, id: string, on: boolean): Promise<void> {
  const before = qc.getQueryData<SpotFavoriteRow[]>(SPOT_FAVORITES_KEY)
  if (!on) qc.setQueryData<SpotFavoriteRow[]>(SPOT_FAVORITES_KEY, (old) => old?.filter((r) => r.id !== id))
  try {
    await (on ? api.spotFavorite(id) : api.spotUnfavorite(id))
  } catch (e) {
    qc.setQueryData(SPOT_FAVORITES_KEY, before)
    throw e
  } finally {
    await Promise.all([
      qc.invalidateQueries({ queryKey: spotKey(id) }),
      qc.invalidateQueries({ queryKey: SPOT_FAVORITES_KEY }),
      // The grid under Favorites lists favorite spots (bugs.md).
      qc.invalidateQueries({ queryKey: ['spots-near'] }),
    ])
  }
}
